import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import './Contact.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';

const segmentOptions = [
  { value: '', label: 'Select a segment' },
  { value: 'options', label: 'Options' },
  { value: 'equity', label: 'Equity' },
  { value: 'banknifty', label: 'BankNifty (Call-Put)' },
  { value: 'nifty50', label: 'Nifty50' },
  { value: 'finnifty', label: 'Finnifty' },
  { value: 'midcapnifty', label: 'MidcapNifty' },
  { value: 'sensex', label: 'Sensex' },
];

const capitalOptions = [
  { value: '', label: 'Select an amount' },
  { value: '30000-50000', label: '₹30,000 – ₹50,000' },
  { value: '1-3-lakh', label: '₹1 Lakh – ₹3 Lakh' },
  { value: '5-9-lakh', label: '₹5 Lakh – ₹9 Lakh' },
  { value: '10-15-lakh', label: '₹10 Lakh – ₹15 Lakh' },
  { value: 'more-than-20-lakh', label: 'More than ₹20 Lakh' },
];

const socials = [
  { Icon: WhatsAppIcon, href: 'https://wa.me/919510980382', label: 'WhatsApp', color: '#25d366' },
  { Icon: FacebookIcon, href: 'https://facebook.com', label: 'Facebook', color: '#1877f2' },
  { Icon: InstagramIcon, href: 'https://instagram.com', label: 'Instagram', color: '#e4405f' },
  { Icon: LinkedInIcon, href: 'https://linkedin.com', label: 'LinkedIn', color: '#0a66c2' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    background: '#fff',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(11,36,71,0.12)',
      borderWidth: 1.5,
    },
    '&:hover fieldset': { borderColor: 'rgba(239,106,29,0.4)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    segment: '',
    capital: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone' && !/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const next = { ...errors };
    switch (name) {
      case 'name':
        next.name = value ? '' : 'Name is required';
        break;
      case 'email':
        next.email = value
          ? /\S+@\S+\.\S+/.test(value)
            ? ''
            : 'Email is invalid'
          : 'Email is required';
        break;
      case 'phone':
        next.phone = value
          ? value.length === 10
            ? ''
            : 'Phone must be a 10-digit number'
          : 'Phone is required';
        break;
      case 'segment':
        next.segment = value ? '' : 'Segment is required';
        break;
      case 'capital':
        next.capital = value ? '' : 'Capital investment is required';
        break;
      case 'message':
        next.message = value ? '' : 'Message is required';
        break;
      default:
        break;
    }
    setErrors(next);
  };

  const validate = () => {
    const t = {};
    if (!formData.name) t.name = 'Name is required';
    if (!formData.email) t.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) t.email = 'Email is invalid';
    if (!formData.phone) t.phone = 'Phone is required';
    else if (formData.phone.length !== 10) t.phone = 'Phone must be a 10-digit number';
    if (!formData.segment) t.segment = 'Segment is required';
    if (!formData.capital) t.capital = 'Capital investment is required';
    if (!formData.message) t.message = 'Message is required';
    setErrors(t);
    return Object.keys(t).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.post(
        'https://realstock.onrender.com/api/userData',
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          segment: formData.segment,
          investment: parseFloat(formData.capital.replace(/[^0-9.]/g, '')),
          message: formData.message,
        }
      );
      console.log(response.data);
      alert('Form submitted successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        segment: '',
        capital: '',
        message: '',
      });
    } catch (err) {
      console.error('There was an error submitting the form!', err);
      alert('Failed to submit the form. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const isValid = (field) => formData[field] && !errors[field];

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        overflow: 'hidden',
        /* Rich mesh gradient: 5 overlapping blobs in brand colors */
        background: `
          radial-gradient(720px 520px at 88% 12%, rgba(239,106,29,0.22) 0%, rgba(239,106,29,0) 60%),
          radial-gradient(680px 500px at 8% 88%, rgba(59,130,246,0.20) 0%, rgba(59,130,246,0) 60%),
          radial-gradient(620px 480px at 92% 92%, rgba(168,85,247,0.16) 0%, rgba(168,85,247,0) 60%),
          radial-gradient(640px 460px at 6% 14%, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0) 60%),
          radial-gradient(900px 700px at 50% 50%, rgba(11,36,71,0.06) 0%, rgba(11,36,71,0) 70%),
          linear-gradient(180deg, #ffffff 0%, #f1f5f9 45%, #ffffff 100%)
        `,
      }}
    >
      {/* Large soft blurred blob — top-right */}
      <Box
        component={motion.div}
        animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: -200,
          right: -150,
          width: 520,
          height: 520,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,106,29,0.28) 0%, rgba(239,106,29,0) 65%)',
          filter: 'blur(40px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      {/* Large soft blurred blob — bottom-left */}
      <Box
        component={motion.div}
        animate={{ x: [0, -25, 0], y: [0, -18, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          bottom: -220,
          left: -180,
          width: 580,
          height: 580,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0) 65%)',
          filter: 'blur(50px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Diagonal blueprint line pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            repeating-linear-gradient(45deg,
              transparent 0,
              transparent 38px,
              rgba(11,36,71,0.045) 38px,
              rgba(11,36,71,0.045) 39px
            )
          `,
          maskImage:
            'radial-gradient(ellipse at center, #000 20%, transparent 85%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, #000 20%, transparent 85%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Small floating orb — orange */}
      <Box
        component={motion.div}
        animate={{ y: [0, -18, 0], x: [0, 8, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: 140,
          right: '8%',
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: ACCENT,
          opacity: 0.55,
          boxShadow: `0 0 30px ${ACCENT}`,
          zIndex: 0,
        }}
      />
      {/* Small floating orb — blue */}
      <Box
        component={motion.div}
        animate={{ y: [0, 20, 0], x: [0, -8, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          bottom: 180,
          left: '6%',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: '#3b82f6',
          opacity: 0.6,
          boxShadow: '0 0 24px #3b82f6',
          zIndex: 0,
        }}
      />
      {/* Small floating orb — purple */}
      <Box
        component={motion.div}
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        sx={{
          position: 'absolute',
          top: '55%',
          right: '4%',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: '#a855f7',
          opacity: 0.5,
          boxShadow: '0 0 18px #a855f7',
          zIndex: 0,
        }}
      />
      <Container
        maxWidth={false}
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 4, md: 6, lg: 8 },
        }}
      >
        {/* Section header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 }, maxWidth: 1400, mx: 'auto' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.8,
              py: 0.6,
              mb: 2.5,
              borderRadius: 5,
              background: 'rgba(239,106,29,0.1)',
              border: '1px solid rgba(239,106,29,0.25)',
              color: ACCENT,
              fontSize: '0.82rem',
              fontWeight: 700,
              letterSpacing: 0.7,
            }}
          >
            <MessageIcon sx={{ fontSize: '0.95rem' }} />
            GET IN TOUCH
          </Box>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem', lg: '3.6rem' },
              fontWeight: 800,
              color: NAVY,
              lineHeight: 1.1,
              mb: 2,
              letterSpacing: -0.8,
            }}
          >
            Let&apos;s start a{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              conversation
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 680,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '0.98rem', md: '1.12rem' },
              lineHeight: 1.7,
            }}
          >
            Tell us a little about your trading goals and we&apos;ll get back to
            you within hours — usually much sooner.
          </Box>
        </Box>

        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          alignItems="stretch"
          sx={{ maxWidth: 1500, mx: 'auto' }}
        >
          {/* Contact info card */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderRadius: 4,
                  p: { xs: 3.5, md: 4 },
                  color: '#fff',
                  background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY} 50%, #123a73 100%)`,
                  boxShadow: '0 30px 60px -20px rgba(11,36,71,0.45)',
                  overflow: 'hidden',
                  /* Grid pattern */
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundImage:
                      'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    maskImage:
                      'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                    WebkitMaskImage:
                      'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                    pointerEvents: 'none',
                  },
                  /* Orange glow */
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -100,
                    right: -100,
                    width: 280,
                    height: 280,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(239,106,29,0.28) 0%, rgba(239,106,29,0) 70%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      mb: 0.5,
                      letterSpacing: -0.2,
                    }}
                  >
                    Contact Information
                  </Box>
                  <Box
                    sx={{
                      fontSize: '0.93rem',
                      color: '#cbd5e1',
                      mb: 4,
                      lineHeight: 1.6,
                    }}
                  >
                    Reach out via any channel below. We&apos;re live during market
                    hours.
                  </Box>

                  <Stack spacing={2.5}>
                    <ContactRow
                      Icon={PhoneIcon}
                      label="Phone"
                      value="+91 95109 80382"
                      href="tel:+919510980382"
                    />
                    <ContactRow
                      Icon={EmailIcon}
                      label="Email"
                      value="admin@realstockexperts.in"
                      href="mailto:admin@realstockexperts.in"
                    />
                    <ContactRow
                      Icon={AccessTimeIcon}
                      label="Office Hours"
                      value="Mon – Sat · 9:00 AM – 6:00 PM"
                    />
                    <ContactRow
                      Icon={LocationOnIcon}
                      label="Address"
                      value="Mumbai, Maharashtra, India"
                    />
                  </Stack>

                  {/* Socials */}
                  <Box sx={{ mt: 5 }}>
                    <Box
                      sx={{
                        fontSize: '0.72rem',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        fontWeight: 600,
                        mb: 1.5,
                      }}
                    >
                      Follow Us
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {socials.map(({ Icon, href, label, color }) => (
                        <IconButton
                          key={label}
                          component="a"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          sx={{
                            color: '#fff',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            width: 40,
                            height: 40,
                            transition: 'all 0.25s ease',
                            '& svg': { fontSize: '1.1rem' },
                            '&:hover': {
                              background: color,
                              borderColor: color,
                              transform: 'translateY(-3px)',
                              boxShadow: `0 10px 22px ${color}55`,
                            },
                          }}
                        >
                          <Icon />
                        </IconButton>
                      ))}
                    </Stack>
                  </Box>

                  {/* Promise badge */}
                  <Box
                    sx={{
                      mt: 5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.8,
                      borderRadius: 5,
                      background: 'rgba(34,197,94,0.12)',
                      border: '1px solid rgba(34,197,94,0.3)',
                      color: '#86efac',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#22c55e',
                        boxShadow: '0 0 0 4px rgba(34,197,94,0.2)',
                      }}
                    />
                    Avg. response in 5 minutes
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Form card */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  p: { xs: 3, md: 4.5 },
                  background: '#fff',
                  border: '1px solid rgba(11,36,71,0.06)',
                  boxShadow: '0 30px 60px -20px rgba(11,36,71,0.18)',
                }}
              >
                <Box
                  sx={{
                    fontSize: { xs: '1.4rem', md: '1.6rem' },
                    fontWeight: 800,
                    color: NAVY,
                    mb: 0.5,
                    letterSpacing: -0.2,
                  }}
                >
                  Send us a message
                </Box>
                <Box
                  sx={{
                    fontSize: '0.88rem',
                    color: '#64748b',
                    mb: 3.5,
                  }}
                >
                  Fields marked with <Box component="span" sx={{ color: ACCENT, fontWeight: 700 }}>*</Box> are required
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name *"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: isValid('name') && (
                          <InputAdornment position="end">
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: isValid('email') && (
                          <InputAdornment position="end">
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number *"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: isValid('phone') && formData.phone.length === 10 && (
                          <InputAdornment position="end">
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Trading Segment *"
                      name="segment"
                      value={formData.segment}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors.segment}
                      helperText={errors.segment || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShowChartIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    >
                      {segmentOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.value === ''}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Capital Investment *"
                      name="capital"
                      value={formData.capital}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors.capital}
                      helperText={errors.capital || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalanceWalletIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    >
                      {capitalOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.value === ''}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Your Message *"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      multiline
                      minRows={4}
                      fullWidth
                      error={!!errors.message}
                      helperText={errors.message || ' '}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <MessageIcon sx={{ color: '#94a3b8', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  disabled={loading}
                  size="large"
                  endIcon={
                    loading ? (
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    mt: 1,
                    background: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1.4,
                    fontSize: '1rem',
                    borderRadius: 2,
                    boxShadow: '0 12px 28px rgba(239,106,29,0.4)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      background: ACCENT_HOVER,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 16px 32px rgba(239,106,29,0.5)',
                    },
                    '&.Mui-disabled': {
                      background: ACCENT,
                      color: '#fff',
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? 'Sending…' : 'Send Message'}
                </Button>

                <Box
                  sx={{
                    mt: 2,
                    fontSize: '0.78rem',
                    color: '#94a3b8',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.6,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#22c55e' }} />
                  Your details stay 100% confidential
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

const ContactRow = ({ Icon, label, value, href }) => {
  const valueProps = href
    ? { component: 'a', href, sx: { '&:hover': { color: ACCENT } } }
    : {};

  return (
    <Box sx={{ display: 'flex', gap: 1.6, alignItems: 'flex-start' }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 1.5,
          background: 'rgba(239,106,29,0.14)',
          color: ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(239,106,29,0.25)',
          '& svg': { fontSize: '1.15rem' },
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            fontSize: '0.72rem',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            mb: 0.3,
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
          }}
        >
          {label}
          {href && <ChevronRightIcon sx={{ fontSize: '0.85rem' }} />}
        </Box>
        <Box
          {...valueProps}
          sx={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: '#fff',
            textDecoration: 'none',
            display: 'block',
            transition: 'color 0.2s ease',
            ...(valueProps.sx || {}),
          }}
        >
          {value}
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
